/**
 * Hedef sihirbazının saf kuralları. Mobildeki components/goals/unitRules.ts,
 * utils/goalPlan.ts, utils/goalSummary.ts ve GoalWizard'daki adım
 * doğrulamasıyla aynı; sunucudaki src/modules/work/domain/{units,goalPlan,
 * weekDays,goalKey}.js ile de uyumlu. Ekranda görünen sayı ile kaydedilen
 * aynı olsun diye hesaplar burada tek yerde.
 */

/** Sunucudaki UNITS ile aynı sırada. */
export const WORK_UNITS = ["minute", "count", "page", "line", "tally"];
export const WORK_ACTIVITIES = ["okuma", "ezber", "yazma", "dinleme", "calisma"];

/**
 * Hangi yapılış biçiminde hangi birimler anlamlı, hangi sırayla.
 * İlk eleman o biçim için önerilen (varsayılan) birimdir.
 */
export const ACTIVITY_UNITS = {
  okuma: ["page", "count", "minute", "line", "tally"],
  ezber: ["line", "page", "count", "tally", "minute"],
  yazma: ["line", "page", "minute", "count"],
  dinleme: ["minute", "count", "page"],
  calisma: ["minute"],
};

/** Eser bilgisi yokken gösterilen biçimler; çalışma yalnız Dil eserlerinde. */
const GENERAL_ACTIVITIES = ["okuma", "ezber", "yazma", "dinleme"];

/**
 * Bir eserin desteklediği biçimler; eser bilgisi yoksa genel dört biçim.
 * `current` verilirse (düzenlenen hedefin biçimi) listede kalır.
 */
export function allowedActivities(workActivities, current) {
  let list = GENERAL_ACTIVITIES;
  if (Array.isArray(workActivities) && workActivities.length) {
    const filtered = WORK_ACTIVITIES.filter((a) => workActivities.includes(a));
    if (filtered.length) list = filtered;
  }
  if (current && !list.includes(current)) {
    list = WORK_ACTIVITIES.filter((a) => list.includes(a) || a === current);
  }
  return list;
}

/**
 * Seçilen biçim ve esere göre birim listesi. Külliyatta (Risale-i Nur gibi)
 * adet ve sayaç çıkar (adetle takip edilen genel adlarda yalnız sayaç);
 * müstakil eserde okurken satır çıkar. Eser kısıtıyla kesişim boşsa eser
 * kısıtı yok sayılır, kullanıcı seçimsiz kalmasın.
 */
export function allowedUnits(activity, workUnits, isCollection = false) {
  let byActivity = ACTIVITY_UNITS[activity] || ACTIVITY_UNITS.okuma;
  const countable = Array.isArray(workUnits) && workUnits.includes("count");
  if (isCollection && !countable) {
    byActivity = byActivity.filter((unit) => unit !== "count" && unit !== "tally");
  } else if (isCollection) {
    byActivity = byActivity.filter((unit) => unit !== "tally");
  } else if (activity === "okuma") {
    byActivity = byActivity.filter((unit) => unit !== "line");
  }
  if (!Array.isArray(workUnits) || workUnits.length === 0) return byActivity;
  const intersection = byActivity.filter((unit) => workUnits.includes(unit));
  return intersection.length ? intersection : byActivity;
}

/** Seçili birim artık uygun değilse önerilene düşürür. */
export function resolveUnit(current, activity, workUnits, isCollection = false) {
  const allowed = allowedUnits(activity, workUnits, isCollection);
  return allowed.includes(current) ? current : allowed[0];
}

/** Seçili biçim eserde tanımlı değilse ilk uygun biçime düşürür. */
export function resolveActivity(current, workActivities) {
  const allowed = allowedActivities(workActivities);
  return allowed.includes(current) ? current : allowed[0];
}

/**
 * Plan hesabı (sunucudaki resolveGoalPlan ile aynı): günlük hedef aşağı
 * yuvarlanır, tam bölünmeyen artık birer gün fazladan yapılır. Her güne en
 * az 1 düşmeli: 10 günde 5 sayfa denirse plan 5 güne iner.
 */
export function computeGoalPlan(totalValue, planDays) {
  const total = Math.floor(Number(totalValue));
  const days = Math.floor(Number(planDays));
  if (!Number.isFinite(total) || !Number.isFinite(days) || total <= 0 || days <= 0) {
    return { goalValue: 0, planDays: 0, extraDays: 0, extraValue: 0 };
  }
  const cappedDays = Math.min(days, total);
  const goalValue = Math.max(1, Math.floor(total / cappedDays));
  const extraDays = Math.max(0, total - goalValue * cappedDays);
  return {
    goalValue,
    planDays: cappedDays,
    extraDays,
    extraValue: extraDays > 0 ? goalValue + 1 : 0,
  };
}

/** Hedef kimliği: ad + biçim (biçimi olmayan eski hedef okuma sayılır). */
export function goalKey(label, activity) {
  const act = WORK_ACTIVITIES.includes(String(activity)) ? String(activity) : "okuma";
  return `${String(label ?? "").trim()}::${act}`;
}

export function isSameGoal(entry, label, activity) {
  if (!entry?.label) return false;
  return goalKey(entry.label, entry.activity) === goalKey(label, activity);
}

/** Listede aynı ad + biçimde iki hedef var mı. */
export function hasDuplicateGoal(items) {
  const seen = new Set();
  for (const item of items || []) {
    const key = goalKey(item.label, item.activity);
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}

/** Yalnız rakamları sayıya çevirir; boşsa null. */
export function toInt(text) {
  const n = parseInt(String(text ?? "").replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

/** Sihirbazın adımları. Nokta göstergesi ve atlama bu sıraya göre. */
export const STEP_COUNT = 6;
export const STEP = { WORK: 0, ACTIVITY: 1, UNIT: 2, DAYS: 3, AMOUNT: 4, SUMMARY: 5 };

/**
 * Sihirbazın o anki durumundan türeyen değerler.
 *
 * Gün sayısı her hedefte sorulur; anlamı esere göre değişir:
 * - Müstakil eserde (dakika dışı) bir bitirme planıdır: miktar toplamdır,
 *   günlük hedef toplam / gün. Sayfa ve satırda gün zorunlu.
 * - Külliyatta ya da dakikalı hedefte isteğe bağlı: miktar her zaman
 *   günlüktür, gün yalnızca kaç gün süreceğini söyler.
 */
export function deriveWizard({ work, unit, planDays, amount }) {
  const days = toInt(planDays);
  const total = toInt(amount);
  const isTotalPlan = !!work && !work.isCollection && unit !== "minute";
  const planRequired = isTotalPlan && (unit === "page" || unit === "line");
  const hasDuration = days !== null && days >= 1;
  const hasPlan = isTotalPlan && hasDuration;
  const plan = hasPlan && total && days ? computeGoalPlan(total, days) : null;
  const dailyTarget = plan ? plan.goalValue : total;
  return { days, total, isTotalPlan, planRequired, hasDuration, hasPlan, plan, dailyTarget };
}

/**
 * Adımdan ileri geçilebilir mi; geçilemezse gösterilecek hata anahtarı.
 * Mobildeki canContinue + handleNext ile aynı kurallar.
 */
export function stepError(step, state) {
  const { mode, label, work, planDays } = state;
  const d = deriveWizard(state);
  if (step === STEP.WORK) {
    // Özel isim yok: yeni hedef kütüphaneden bir esere bağlanır.
    const ok = mode === "edit" ? String(label || "").trim().length >= 3 : !!work;
    return ok ? null : "gw_error_pick_work";
  }
  if (step === STEP.DAYS) {
    if (d.planRequired) return d.days !== null && d.days >= 1 ? null : "gw_error_days_required";
    const blank = !String(planDays ?? "").trim();
    return blank || (d.days !== null && d.days >= 1) ? null : "gw_error_days_min";
  }
  if (step === STEP.AMOUNT) {
    if (d.total === null || d.total <= 0) return "gw_error_amount";
    // Bitirme planında her güne en az 1 düşmeli.
    if (d.hasPlan && d.days && d.total < d.days) return "gw_error_amount_below_days";
    return null;
  }
  return null;
}

/**
 * Tek seçenekli adım atlanır (Dil seçilince biçim yalnız çalışma, birim
 * yalnız süre); kullanıcı tek şıklı ekranda boşuna ileri basmasın.
 */
export function nextStep(from, direction, { activityCount, unitCount }) {
  const skippable = (s) =>
    (s === STEP.ACTIVITY && activityCount === 1) || (s === STEP.UNIT && unitCount === 1);
  let next = from + direction;
  while (next > 0 && next < STEP_COUNT - 1 && skippable(next)) next += direction;
  return Math.max(0, Math.min(STEP_COUNT - 1, next));
}

/** Sihirbazın sonucu (mobil GoalWizardValue ile aynı alanlar). */
export function buildWizardValue(state) {
  const { label, unit, activity, work, weekDays } = state;
  const d = deriveWizard(state);
  return {
    label: String(label || "").trim(),
    unit,
    activity,
    workId: work?.id ?? null,
    workName: work?.name,
    // Plan varsa toplam + gün gider, sunucu günlük hedefi hesaplar.
    totalValue: d.hasPlan ? d.total : null,
    planDays: d.hasDuration ? d.days : null,
    goalValue: d.hasPlan ? null : d.total,
    weekDays: [...(weekDays || [])],
  };
}

/** Kaydedilecek günlük hedef: bitirme planında toplam / gün, değilse girilen. */
export function dailyValueOf(value) {
  return value.planDays && value.totalValue
    ? computeGoalPlan(value.totalValue, value.planDays).goalValue
    : (value.goalValue ?? 0);
}

/** Dolu takip alanları; boşlar hiç yazılmaz (sunucu null'ı zaten düşürür). */
function trackingOf(value) {
  const out = {};
  if (value.unit) out.unit = value.unit;
  if (value.activity) out.activity = value.activity;
  if (value.workId) out.workId = value.workId;
  if (value.totalValue != null) out.totalValue = value.totalValue;
  if (value.planDays != null) out.planDays = value.planDays;
  out.weekDays = Array.isArray(value.weekDays) ? [...value.weekDays] : [];
  return out;
}

/**
 * Hedef ekleme/güncelleme isteğinin gövdesi (addMyUserGoal, add-user-goal
 * ...). Düzenlemede ad değişmez: hedef ad + biçimle bulunur.
 */
export function toGoalRequest(value, { label } = {}) {
  return {
    goalLabel: String(label ?? value.label).trim(),
    goalValue: dailyValueOf(value),
    ...trackingOf(value),
  };
}

/** Sihirbaz sonucu QR şablon kalemine: günlük hedef defaultValue olur. */
export function toQrItem(value) {
  const item = { label: String(value.label).trim(), defaultValue: dailyValueOf(value), ...trackingOf(value) };
  if (value.workName) item.workName = value.workName;
  if (!item.weekDays.length) delete item.weekDays;
  return item;
}

/** Eserin kimliği: hedefte metin, bazen doldurulmuş nesne gelebilir. */
function workIdOf(work) {
  if (!work) return null;
  if (typeof work === "object") return work._id ? String(work._id) : work.id ? String(work.id) : null;
  return String(work);
}

/** Kayıtlı hedef (goalDetail) ya da QR kalemi → sihirbazın başlangıç değeri. */
export function goalToInitial(goal) {
  if (!goal) return {};
  return {
    label: goal.label || "",
    unit: WORK_UNITS.includes(goal.unit) ? goal.unit : "count",
    activity: WORK_ACTIVITIES.includes(goal.activity) ? goal.activity : "okuma",
    workId: workIdOf(goal.work) || goal.workId || null,
    workName: goal.workName,
    goalValue: goal.goalValue ?? goal.defaultValue ?? null,
    totalValue: goal.totalValue ?? null,
    planDays: goal.planDays ?? null,
    weekDays: Array.isArray(goal.weekDays) ? [...goal.weekDays] : [],
  };
}

/** Birim ve biçim adları (çeviri anahtarları). */
export const UNIT_LABEL_KEYS = {
  minute: "gw_unit_minute",
  count: "gw_unit_count",
  page: "gw_unit_page",
  line: "gw_unit_line",
  tally: "gw_unit_tally",
};
export const UNIT_HINT_KEYS = {
  minute: "gw_unit_minute_hint",
  count: "gw_unit_count_hint",
  page: "gw_unit_page_hint",
  line: "gw_unit_line_hint",
  tally: "gw_unit_tally_hint",
};
/** Sayının yanına yazılan birim adı: "3 sayfa", "5 satır". */
export const UNIT_WORD_KEYS = {
  minute: "gw_word_minute",
  count: "gw_word_count",
  page: "gw_word_page",
  line: "gw_word_line",
  tally: "gw_word_count",
};
export const ACTIVITY_LABEL_KEYS = {
  okuma: "gw_activity_okuma",
  ezber: "gw_activity_ezber",
  yazma: "gw_activity_yazma",
  dinleme: "gw_activity_dinleme",
  calisma: "gw_activity_calisma",
};

/** Haftanın günleri, pazartesiden; değerler JavaScript'teki gibi (0 = Pazar). */
export const WEEK_DAYS = [
  { value: 1, key: "gw_day_mon" },
  { value: 2, key: "gw_day_tue" },
  { value: 3, key: "gw_day_wed" },
  { value: 4, key: "gw_day_thu" },
  { value: 5, key: "gw_day_fri" },
  { value: 6, key: "gw_day_sat" },
  { value: 0, key: "gw_day_sun" },
];

/** Gün seçimi: yedi günün tamamı "her gün" (boş liste) sayılır. */
export function toggleWeekDay(weekDays, day) {
  const list = weekDays || [];
  const next = list.includes(day) ? list.filter((d) => d !== day) : [...list, day].sort((a, b) => a - b);
  return next.length === 7 ? [] : next;
}

/** "Pzt, Çar, Cum" ya da "Her gün". */
export function formatWeekDays(weekDays, t) {
  if (!Array.isArray(weekDays) || weekDays.length === 0) return t("gw_every_day");
  return weekDays
    .slice()
    .sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b))
    .map((d) => t(WEEK_DAYS.find((w) => w.value === d)?.key || "gw_every_day"))
    .join(", ");
}

export function unitWord(unit, t) {
  return t(UNIT_WORD_KEYS[unit] || "gw_word_count");
}

/**
 * Liste satırının takip özeti (mobil buildGoalSummary gibi):
 * "Sayfa · Okuma", seçiliyse günler, planlıysa "600 / 60 gün · günlük 10".
 * Eski hedeflerde alanlar boş olabilir; o zaman satır üretilmez.
 */
export function goalSummaryLines(goal, t) {
  const lines = [];
  const parts = [];
  if (goal.unit && UNIT_LABEL_KEYS[goal.unit]) parts.push(t(UNIT_LABEL_KEYS[goal.unit]));
  if (goal.activity && ACTIVITY_LABEL_KEYS[goal.activity]) parts.push(t(ACTIVITY_LABEL_KEYS[goal.activity]));
  if (parts.length) lines.push(parts.join(" · "));
  if (Array.isArray(goal.weekDays) && goal.weekDays.length) lines.push(formatWeekDays(goal.weekDays, t));
  if (goal.planDays && goal.totalValue) {
    const base = goal.goalValue ?? 0;
    const extraDays = Math.max(0, goal.totalValue - base * goal.planDays);
    const extra = extraDays ? ` (${extraDays} ${t("gw_days")} ${base + 1})` : "";
    lines.push(`${goal.totalValue} / ${goal.planDays} ${t("gw_days")} · ${t("gw_daily_goal")} ${base}${extra}`);
  } else if (goal.planDays) {
    lines.push(`${goal.planDays} ${t("gw_days")}`);
  }
  return lines;
}

/** Satırın sağındaki değer: "10 sayfa"; birimi olmayan eski hedefte yalnız sayı. */
export function goalValueText(goal, t) {
  const value = goal.goalValue ?? goal.defaultValue ?? 0;
  return goal.unit ? `${value} ${unitWord(goal.unit, t)}` : String(value);
}

/** works/list gövdesi (mobil listWorks gibi): arama varsa üst eser yok sayılır. */
export function worksListBody({ category, parentId, search } = {}) {
  const body = {};
  if (category) body.category = category;
  if (search) body.search = search;
  else if (parentId !== undefined) body.parentId = parentId ?? "";
  return body;
}
